import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Castelnau-le-Lez' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Castelnau-le-Lez',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Castelnau-le-Lez' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Castelnau-le-Lez. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous dans le quartier Eurêka à Castelnau-le-Lez ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons dans tout Castelnau-le-Lez : Eurêka, le Devois, Sablassou, centre-ville. Nos techniciens connaissent les spécificités des résidences modernes comme des maisons anciennes. Délai 24-48h, 7j/7.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les volets roulants électriques à Castelnau ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Nous réparons tous les types de volets roulants : électriques filaires et radio, manuels, solaires. Diagnostic gratuit, devis sans engagement et intervention rapide sous garantie 10 ans.',
      },
    },
  ],
};

export default function CastelnauLeLezPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Castelnau | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Castelnau-le-Lez ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-castelnau-le-lez/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Castelnau-le-Lez"
        subtitle="Limitrophe de Montpellier, Castelnau-le-Lez mêle centre ancien et résidences modernes. Baies, volets, vitrages : nos techniciens interviennent 24-48h, devis gratuit."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Une menuiserie en panne à Castelnau-le-Lez ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent dans tout Castelnau-le-Lez, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Castelnau-le-Lez, nous intervenons sur tous les types de menuiseries : des baies coulissantes des résidences modernes d'Eurêka aux fenêtres des maisons du centre ancien. Diagnostic gratuit au <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/castelnau-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Castelnau-le-Lez"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : rail, roulettes, réglage</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les résidences longeant le Lez sont équipées de baies coulissantes qui s'usent avec le temps. Roulettes encrassées, rail déformé, <Link to="/serrure-cremone-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">serrure</Link> qui ne verrouille plus : nous réparons tout.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes et galets</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>. Garantie 10 ans.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant : blocage, moteur, motorisation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Volets roulants</Link> en panne : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">blocage</Link>, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> hors service, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link> à remplacer. Nous assurons la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Côté stores : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link>, <Link to="/store-electrique-herault/" className="text-accent-500 font-semibold hover:underline">électriques</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>. Diagnostic gratuit.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/castelnau-volet-02.jpg"
              alt="Réparation de volet roulant à Castelnau-le-Lez"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/castelnau-vitrage-03.webp"
              alt="Remplacement de vitrage à Castelnau-le-Lez"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre : tous types de réparations</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> pour <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, pose de <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> et <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">urgence</Link> 7j/7. <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">Vitrage embué</Link> : on remplace le vitrage, pas la fenêtre.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémone</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link>, <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">oscillo-battant</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Castelnau-le-Lez sous 24 à 48h</h2>
        <p>
          Nous couvrons tous les quartiers : centre-ville, Eurêka, le Devois, Sablassou et abords du Lez. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, Clapiers, Jacou, <Link to="/depannage-menuiserie-le-cres/" className="text-accent-500 font-semibold hover:underline">Le Crès</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}